import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Heart, Users, Calendar, FileText, Shield, TrendingUp } from 'lucide-react';
import AuthDialog from '../components/AuthDialog';

const LandingPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);

  React.useEffect(() => {
    if (user && !loading) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <nav className="glass-effect fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_pressed-collab/artifacts/sya4rwpg_Black%20Font%20Transp%20Bg.png" 
              alt="Pressed Logo" 
              className="h-12"
            />
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/about')}
              className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
            >
              About
            </button>
            <Button 
              data-testid="login-btn" 
              onClick={() => setAuthDialogOpen(true)} 
              className="bg-blue-700 hover:bg-blue-800 focus:bg-blue-800 text-white border-2 border-blue-700 hover:border-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto text-center fade-in">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            Caregiving Made
            <span className="block bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent">
              Collaborative
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
            When someone needs care, one person shouldn't bear the entire burden alone. 
            Pressed distributes caregiving responsibilities across your team with privacy controls that work.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button data-testid="get-started-btn" size="lg" onClick={() => setAuthDialogOpen(true)} className="bg-blue-700 hover:bg-blue-800 focus:bg-blue-800 text-white text-lg px-8 py-6 border-2 border-blue-700 hover:border-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2">
              Get Started Free
            </Button>
            <Button data-testid="learn-more-btn" size="lg" variant="outline" onClick={() => navigate('/about')} className="text-lg px-8 py-6 bg-white hover:bg-gray-50 focus:bg-gray-50 text-gray-900 border-2 border-gray-400 hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-offset-2">
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16">
            Everything Your Care Team Needs
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Users className="w-8 h-8" />}
              title="Team Collaboration"
              description="Core, Support, and Provider roles with granular privacy controls. Share what matters, keep what's private."
            />
            <FeatureCard
              icon={<Calendar className="w-8 h-8" />}
              title="Appointment Tracking"
              description="Never miss a doctor visit. Coordinate attendees, track questions to ask, and sync to personal calendars."
            />
            <FeatureCard
              icon={<Heart className="w-8 h-8" />}
              title="Caregiving Shifts"
              description="Coordinate volunteer and professional shifts with approval workflows. No unwelcome surprises."
            />
            <FeatureCard
              icon={<FileText className="w-8 h-8" />}
              title="Document Management"
              description="Store insurance cards, test results, legal documents. Organize by area, share selectively."
            />
            <FeatureCard
              icon={<Shield className="w-8 h-8" />}
              title="Medication Tracking"
              description="Track medications, log adherence, identify patterns. Export reports for doctor visits."
            />
            <FeatureCard
              icon={<TrendingUp className="w-8 h-8" />}
              title="Health Analytics"
              description="Visualize vital signs over time. Spot concerning trends before they become problems."
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16">
            How Pressed Works
          </h2>
          <div className="grid md:grid-cols-3 gap-12">
            <StepCard
              number="1"
              title="Create Your Team"
              description="Set up a team for your loved one. Invite family, friends, and professional caregivers."
            />
            <StepCard
              number="2"
              title="Distribute Responsibilities"
              description="Assign tasks, coordinate shifts, manage providers. Everyone knows what needs doing."
            />
            <StepCard
              number="3"
              title="Stay Connected"
              description="Share updates, coordinate care, maintain privacy. No one feels isolated or overwhelmed."
            />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg text-gray-600 mb-12">
            One subscription covers participation as a Core member on unlimited teams.
            Support team members participate free.
          </p>
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <PricingCard
              title="Monthly"
              price="$9.99"
              period="per month"
              features={[
                "Unlimited teams as Core member",
                "All features included",
                "Priority support",
                "Cancel anytime"
              ]}
              onGetStarted={() => setAuthDialogOpen(true)}
            />
            <PricingCard
              title="Annual"
              price="$99.99"
              period="per year"
              badge="Save 17%"
              features={[
                "Unlimited teams as Core member",
                "All features included",
                "Priority support",
                "Best value"
              ]}
              onGetStarted={() => setAuthDialogOpen(true)}
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 glass-effect">
        <div className="max-w-6xl mx-auto text-center text-gray-600">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_pressed-collab/artifacts/sya4rwpg_Black%20Font%20Transp%20Bg.png" 
              alt="Pressed Logo" 
              className="h-10"
            />
          </div>
          <div className="mb-4">
            <a href="/about" className="text-blue-600 hover:text-blue-700 font-medium">
              About Us
            </a>
          </div>
          <p className="text-sm">
            &copy; {new Date().getFullYear()} Pressed. Empowering families through collaborative caregiving.
          </p>
        </div>
      </footer>

      {/* Auth Dialog */}
      <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} />
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div className="dashboard-card text-center group">
    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-100 to-green-100 flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform">
      {icon}
    </div>
    <h3 className="text-xl font-semibold mb-3">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </div>
);

const StepCard = ({ number, title, description }) => (
  <div className="text-center">
    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-600 to-green-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
      {number}
    </div>
    <h3 className="text-xl font-semibold mb-3">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </div>
);

const PricingCard = ({ title, price, period, badge, features, onGetStarted }) => (
  <div className="dashboard-card relative">
    {badge && (
      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-600 to-green-500 text-white px-4 py-1 rounded-full text-sm font-medium">
        {badge}
      </div>
    )}
    <h3 className="text-2xl font-bold mb-2">{title}</h3>
    <div className="mb-6">
      <span className="text-4xl font-bold">{price}</span>
      <span className="text-gray-600">/{period}</span>
    </div>
    <ul className="space-y-3 mb-8">
      {features.map((feature, i) => (
        <li key={i} className="flex items-center gap-2 text-gray-700">
          <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          {feature}
        </li>
      ))}
    </ul>
    <Button 
      onClick={onGetStarted} 
      className="w-full bg-blue-700 hover:bg-blue-800 focus:bg-blue-800 text-white border-2 border-blue-700 hover:border-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
    >
      Get Started
    </Button>
  </div>
);

export default LandingPage;